import { useMemo } from 'react';

import { generatePath, LoaderFunctionArgs, useNavigate, useParams } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';

import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Button,
  Flex,
  SimpleGrid,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import {
  Badge,
  Campaign,
  CampaignNominee,
  campaignsService,
  FetchCampaignsResponse,
  groupsService,
} from '@aslanpay/portal-common-lib';

import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import CenterSpinner from 'app/components/UI/CenterSpinner';
import useFetchCampaigns from 'app/hooks/useFetchCampaigns';
import useFetchCampaignResults from 'app/hooks/useFetchCampaignResults';
import useGroups from 'app/hooks/useGroups';
import { companyPaths } from 'app/constants/routes';
import { useManagingCompanyStore } from 'app/store';
import { campaignStatusDetails, formatDateTime } from 'app/utils/table/campaigns';

import CancelCampaignModal from '../CancelCampaignModal';

import RecordDecisionModal from './RecordDecisionModal';
import RewordWinnerModal from './RewordWinnerModal';

/** A pride that has run its course has nothing left to cancel. */
const FINISHED: Campaign['status'][] = ['COMPLETED', 'CANCELLED'];

/** The statuses in which nominations can still arrive, so a count read now is a running one. */
const STILL_ARRIVING: Campaign['status'][] = ['DRAFT', 'PENDING', 'VOTING_OPEN'];

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Flex
      bgColor="brand.white"
      borderRadius="10px"
      border="1px solid"
      borderColor="brand.lightGray1"
      w="full"
      p="20px"
      flexDir="column"
      gap="16px"
    >
      <Text as="h5" textStyle="h5SemiBold" color="brand.darkPurple">
        {title}
      </Text>
      {children}
    </Flex>
  );
}

function NomineeRow({ nominee, rank }: { nominee: CampaignNominee; rank: number }) {
  return (
    <AccordionItem data-testid={`nominee-${nominee.nomineeId}`}>
      <AccordionButton px="0">
        <Flex flex="1" align="center" gap="10px">
          <Text textStyle="smallMedium" color="brand.darkGray" w="24px">
            {rank}
          </Text>
          <Text textStyle="smallMedium" color="brand.black">
            {nominee.nomineeName}
          </Text>
          <Badge
            value={`${nominee.nominationCount} ${
              nominee.nominationCount === 1 ? 'nomination' : 'nominations'
            }`}
            status="created"
          />
          {nominee.leavingBeforeAnnouncement && (
            <Badge value="Leaving before announcement" status="left" />
          )}
        </Flex>
        <AccordionIcon />
      </AccordionButton>
      <AccordionPanel px="0">
        <Flex flexDir="column" gap="12px">
          {nominee.nominations.map((nomination) => (
            <Flex
              key={`${nomination.nominatorId}-${nomination.nominationReason ?? ''}`}
              flexDir="column"
              gap="2px"
              borderLeft="2px solid"
              borderColor="brand.lightGray1"
              pl="12px"
            >
              <Text textStyle="extraSmallRegular" color="brand.darkGray">
                {nomination.nominatorName}
              </Text>
              <Text textStyle="smallRegular" color="brand.black">
                {nomination.nominationReason || 'No words were left with this nomination.'}
              </Text>
            </Flex>
          ))}
        </Flex>
      </AccordionPanel>
    </AccordionItem>
  );
}

/**
 * The pride a breadcrumb names, read before the page renders.
 * <p>
 * Naming a pride reads that pride, so the campaign comes back carrying only the one asked about, and its
 * group is looked up to turn an id into the name a reader knows it by.
 */
export const campaignPrideDetailLoader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const companyId = params.companyId as string;
    const request = { companyId, id: params.prideCampaignId, page: 0, size: 1 };

    const page =
      queryClient.getQueryData<FetchCampaignsResponse>(['fetchCampaigns', request]) ??
      (await queryClient.fetchQuery({
        queryKey: ['fetchCampaigns', request],
        queryFn: () => campaignsService.fetchCampaigns(request),
      }));

    const pride = page?.list?.[0]?.children?.[0] ?? page?.list?.[0];
    if (!pride?.groupId) return null;

    const groups = await queryClient.fetchQuery({
      queryKey: ['campaignPrideName', companyId],
      queryFn: () => groupsService.fetchGroups({ companyId, page: 0, size: 9999 }),
      staleTime: 60_000,
    });

    return groups.list.find((group: { id: string }) => group.id === pride.groupId) ?? null;
  };

function CampaignPrideDetail() {
  const navigate = useNavigate();
  const deciding = useDisclosure();
  const rewording = useDisclosure();
  const cancelling = useDisclosure();
  const { campaignId, prideCampaignId } = useParams();
  const company = useManagingCompanyStore((state) => state.company);
  const companyId = company?.id || '';

  // Naming a pride reads that pride: the cycle comes back carrying only the one asked about.
  const { data: campaignsPage, isInitialLoading } = useFetchCampaigns({
    companyId,
    id: prideCampaignId,
    page: 0,
    size: 1,
  });
  const cycle = campaignsPage?.list?.[0];
  const pride = cycle?.children?.[0] ?? cycle;

  // An administrator reads results at any point, including while nominations are still arriving.
  const {
    data: results,
    isInitialLoading: isLoadingResults,
    isError: resultsFailed,
  } = useFetchCampaignResults(prideCampaignId);

  const { data: groupsPage } = useGroups.useFetchGroups(
    { companyId, page: 0, size: 9999 },
    !!companyId
  );
  const prideName = useMemo(() => {
    if (!pride?.groupId) return 'Whole company';

    return (
      (groupsPage?.list ?? []).find((group) => group.id === pride.groupId)?.name ?? 'Unknown pride'
    );
  }, [groupsPage, pride]);

  if (isInitialLoading) {
    return (
      <AuthenticatedPageWrapper title="Pride">
        <CenterSpinner />
      </AuthenticatedPageWrapper>
    );
  }

  if (!pride) {
    return (
      <AuthenticatedPageWrapper title="Pride">
        <Text color="brand.darkPurple">This pride&apos;s campaign could not be found.</Text>
      </AuthenticatedPageWrapper>
    );
  }

  const status = campaignStatusDetails[pride.status];
  const winner = pride.winners?.[0];
  const nominees = results?.nominees ?? [];
  const stillArriving = STILL_ARRIVING.includes(pride.status);

  return (
    <AuthenticatedPageWrapper title={prideName}>
      <Flex flexDir="column" gap="24px">
        <Flex align="flex-start" justify="space-between" gap="15px">
          <Flex flexDir="column" gap="8px">
            <Flex align="center" gap="10px">
              <Text as="h2" textStyle="h2" color="brand.darkPurple">
                {prideName}
              </Text>
              <Badge value={status.label} status={status.badgeStatus} />
            </Flex>
            <Text color="brand.darkPurple">{cycle?.name}</Text>
            {pride.statusReason && (
              <Text textStyle="extraSmallRegular" color="text.error">
                {pride.statusReason}
              </Text>
            )}
          </Flex>
          <Flex gap="12px">
            {!FINISHED.includes(pride.status) && (
              <Button
                variant="outline"
                colorScheme="red"
                onClick={cancelling.onOpen}
                data-testid="open-cancel-pride"
              >
                Cancel this pride
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() =>
                navigate(
                  generatePath(companyPaths.campaignDetail.path, {
                    companyId,
                    campaignId: campaignId as string,
                  })
                )
              }
              data-testid="back-to-campaign"
            >
              Back to the campaign
            </Button>
          </Flex>
        </Flex>

        <Card title="Decision">
          {pride.status === 'INTERNAL_REVIEW_OPEN' && (
            <Flex>
              <Button onClick={deciding.onOpen} data-testid="open-record-decision">
                Record the decision
              </Button>
            </Flex>
          )}
          {pride.status === 'INTERNAL_REVIEW_CLOSED' && winner && (
            <Flex>
              <Button variant="outline" onClick={rewording.onOpen} data-testid="open-reword-winner">
                Reword this win
              </Button>
            </Flex>
          )}
          {winner ? (
            <SimpleGrid columns={{ base: 1, lg: 2 }} gap="16px">
              <Flex flexDir="column" gap="2px">
                <Text textStyle="extraSmallRegular" color="brand.darkGray">
                  Winner
                </Text>
                <Text textStyle="smallMedium" color="brand.black" data-testid="pride-winner">
                  {`${winner.firstName} ${winner.lastName}`.trim()}
                </Text>
              </Flex>
              <Flex flexDir="column" gap="2px">
                <Text textStyle="extraSmallRegular" color="brand.darkGray">
                  Published with the win
                </Text>
                <Text textStyle="smallRegular" color="brand.black">
                  {winner.winnerMessage || '-'}
                </Text>
              </Flex>
            </SimpleGrid>
          ) : (
            <Text textStyle="smallRegular" color="brand.darkGray">
              {pride.outcome === 'NO_WINNER'
                ? 'This pride decided not to pick a winner.'
                : 'No winner has been picked yet.'}
            </Text>
          )}
          <Flex gap="16px" flexWrap="wrap">
            <Text textStyle="extraSmallRegular" color="brand.darkGray">
              Nominations close {formatDateTime(pride.nominationsCloseAt)} · Review opens{' '}
              {formatDateTime(pride.reviewOpensAt)} · Announced{' '}
              {formatDateTime(pride.announcementAt)}
            </Text>
          </Flex>
          {pride.needsCampaignManager && (
            <Flex>
              <Badge value="Nobody is appointed to decide this pride" status="left" />
            </Flex>
          )}
        </Card>

        <Card title="Nominations">
          {stillArriving && !resultsFailed && (
            <Text textStyle="extraSmallRegular" color="brand.darkGray" data-testid="running-count">
              Nominations are still open, so this is a running count. It can change until they close
              on {formatDateTime(pride.nominationsCloseAt)}.
            </Text>
          )}
          {isLoadingResults && <CenterSpinner />}
          {resultsFailed && !isLoadingResults && (
            <Text
              textStyle="smallRegular"
              color="brand.darkGray"
              data-testid="nominations-unavailable"
            >
              The nominations for this pride could not be read. Please try again.
            </Text>
          )}
          {!isLoadingResults && !resultsFailed && nominees.length === 0 && (
            <Text textStyle="smallRegular" color="brand.darkGray" data-testid="no-nominations">
              {stillArriving
                ? 'Nobody has been nominated yet.'
                : 'Nobody was nominated in this pride.'}
            </Text>
          )}
          {!isLoadingResults && !resultsFailed && nominees.length > 0 && (
            <Accordion allowMultiple data-testid="nominees">
              {nominees.map((nominee, index) => (
                <NomineeRow key={nominee.nomineeId} nominee={nominee} rank={index + 1} />
              ))}
            </Accordion>
          )}
        </Card>
      </Flex>

      <CancelCampaignModal
        isOpen={cancelling.isOpen}
        onClose={cancelling.onClose}
        campaignId={pride.id}
        title={`Cancel ${prideName}?`}
        summary={`${prideName} stops. Every other pride of this campaign carries on.`}
        successDescription={`${prideName} has stopped. The rest of the campaign carries on.`}
      />

      {/* Both are mounted only while open: each holds what the admin typed, and a closed dialog that
          keeps it reads as a decision already recorded. */}
      {deciding.isOpen && (
        <RecordDecisionModal
          isOpen
          onClose={deciding.onClose}
          pride={pride}
          prideName={prideName}
          nominees={nominees}
          companyId={companyId}
        />
      )}
      {winner && rewording.isOpen && (
        <RewordWinnerModal
          isOpen
          onClose={rewording.onClose}
          pride={pride}
          winner={winner}
          companyId={companyId}
        />
      )}
    </AuthenticatedPageWrapper>
  );
}

export default CampaignPrideDetail;
